import React, { useCallback, Suspense } from 'react';
import { ErrorBoundary } from 'utils/error';
import { useRouteMatch } from 'react-router-dom';

import ReactTooltip from 'react-tooltip';

import Sidebar from 'modules/fleet/sidebar';
import FleetFilters from 'modules/fleet/filters';

import { DispatchingProvider } from 'modules/dispatching/provider';
import DispatchingFilters from 'modules/dispatching/filters';

import { DispatchingListEmpty } from 'modules/dispatching/list';

import DispatchingAssignment from 'modules/dispatching/assignment';
import { useDispatchingUI } from 'modules/dispatching/ui';

import { useDispatchingFilters } from 'modules/dispatching/service';

import { AssignmentContextProvider } from 'modules/dispatching/assignment/context';
import AssignmentEventEmitter from 'modules/dispatching/assignment/events';

import { AssignmentMapProvider } from 'modules/assignment-map/map/context';
import AssignmentMapEventEmitter from 'modules/assignment-map/events';
import AssignmentMapModal from 'modules/assignment-map';

import { DispatchingRouting, RoutingEventEmitter } from 'modules/routing';

import { BiddingVisibilityHandler } from 'modules/dispatching/bidding-visibility-handler';

import {
  BiddingContextProvider,
  BiddingSection,
  BiddingEventEmitter,
  events as biddingEvents,
} from 'modules/bidding';
import { useSelector } from 'react-redux';
import { constants, getSpecificClientConfig } from 'redux/modules/client-config/selectors';

const DispatchingList = React.lazy(() => import('modules/dispatching/list'));

const t = {
  error: 'Something went wrong',
};

const LoadingFallback = () => <DispatchingListEmpty />;

const ErrorFallback = () => (
  <div className="error-container">
    <div className="error">{t.error}</div>
  </div>
);

const DispatchingAssignmentModule = () => {
  const { filters } = useDispatchingFilters();

  const { setLoading, mutateListFn } = useDispatchingUI();

  const beforeAssignment = useCallback(
    ({ itineraryToAssign }) => {
      setLoading((loading) => ({
        ...loading,
        [itineraryToAssign]: true,
      }));
    },
    [setLoading]
  );

  const afterAssignment = useCallback(
    ({ itineraryToAssign }) => {
      setLoading((loading) => ({
        ...loading,
        [itineraryToAssign]: false,
      }));
      mutateListFn();
    },
    [setLoading, mutateListFn]
  );

  const dispatchingAssignmentSection = useSelector((state) =>
    getSpecificClientConfig(state, constants.DISPATCHING_SECTION)
  );

  if (!dispatchingAssignmentSection) {
    return null;
  }

  return (
    <>
      <DispatchingAssignment
        filters={filters}
        beforeAssignment={beforeAssignment}
        afterAssignment={afterAssignment}
      />
    </>
  );
};

const DispatchingRoutingWithFilters = () => {
  const { filters } = useDispatchingFilters();
  return <DispatchingRouting eventEmitter={RoutingEventEmitter} filters={filters} />;
};

const DispatchingSection = ({ className, parentUrl }) => {
  const match = useRouteMatch();

  const parentURL = match?.url;

  return (
    <>
      <DispatchingProvider>
        <AssignmentContextProvider eventEmitter={AssignmentEventEmitter}>
          <div className={className} data-testid="DispatchingSection">
            <FleetFilters appendFilters={<DispatchingFilters />} />
            <div className="section__wrapper">
              <Sidebar parentUrl={parentUrl} section="dispatching" />
              <div className="section__content">
                <Suspense fallback={<LoadingFallback />}>
                  <ErrorBoundary fallback={<ErrorFallback />}>
                    <DispatchingList parentUrl={parentURL} />
                  </ErrorBoundary>
                </Suspense>
              </div>
              <DispatchingAssignmentModule />
              <DispatchingRoutingWithFilters />
              <BiddingVisibilityHandler
                biddingEventEmitter={BiddingEventEmitter}
                biddingEvents={biddingEvents}
              />
            </div>
          </div>
        </AssignmentContextProvider>
      </DispatchingProvider>
      <AssignmentMapProvider eventEmitter={AssignmentMapEventEmitter}>
        <AssignmentMapModal />
      </AssignmentMapProvider>
      <BiddingContextProvider eventEmitter={BiddingEventEmitter} events={biddingEvents}>
        <BiddingSection />
      </BiddingContextProvider>
      <ReactTooltip id="generic-dispatching-tooltip" type="dark" effect="solid" html={true} />
    </>
  );
};

export default DispatchingSection;
